/**
 * What a viewer may do to this booking, plus the open state of the three
 * drawers. Extracted from `LoadedBooking`, which is now purely layout.
 */
import { useState } from 'react';

import type { BookingDetail } from '../../api/bookingsV2';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../shared/toast/toastContext';
import { Permission } from '../../types/permissions';

import type {
  ResendConfirmationAlert,
  ResendConfirmationOutcome,
} from './resend-confirmation-outcome';

export interface BookingActionGating {
  canRebook: boolean;
  canOverrideCutoff: boolean;
  canRetire: boolean;
  canResendConfirmation: boolean;
  cutoffPassed: boolean;
  rebookOpen: boolean;
  refundOpen: boolean;
  cancelOpen: boolean;
  resendConfirmationOpen: boolean;
  openRebook: () => void;
  closeRebook: () => void;
  closeRefund: () => void;
  closeCancel: () => void;
  openResendConfirmation: () => void;
  closeResendConfirmation: () => void;
  /** Opens whichever retire path applies to this booking. */
  onRetire: () => void;
  activityRefreshToken: number;
  onBookingActionCompleted: () => void;
  /** A settled resend attempt staff has to acknowledge — outlives the dialog
   *  that caused it, same reasoning as `useReservationRowActions`' `alert`. */
  resendConfirmationAlert: ResendConfirmationAlert | null;
  dismissResendConfirmationAlert: () => void;
  /** Closes the dialog, shows the toast/alert and refreshes, per the outcome. */
  onResendConfirmationDone: (outcome: ResendConfirmationOutcome) => void;
}

export function useBookingActionGating(
  booking: BookingDetail,
  reload: () => void,
): BookingActionGating {
  const { hasPermission, hasAnyPermission } = usePermissions();
  const { showToast } = useToast();
  const [rebookOpen, setRebookOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [resendConfirmationOpen, setResendConfirmationOpen] = useState(false);
  const [resendConfirmationAlert, setResendConfirmationAlert] =
    useState<ResendConfirmationAlert | null>(null);
  // `reload` refetches the booking; this is its counterpart for the Activity
  // trail, whose bookingId prop never changes.
  const [activityRefreshToken, setActivityRefreshToken] = useState(0);

  const onBookingActionCompleted = () => {
    reload();
    setActivityRefreshToken((t) => t + 1);
  };

  return {
    canRebook: hasAnyPermission(Permission.REBOOK_BOOKINGS),
    canOverrideCutoff: hasPermission(Permission.OVERRIDE_REBOOK_CUTOFF),
    // One capability for both retire paths — the route enforces this one for a
    // comp cancellation too, since `delete:bookings` is granted to no role.
    canRetire: hasAnyPermission(Permission.REFUND_BOOKINGS),
    // `write:bookings`, not `write:all` — the latter is a backend API-key
    // sentinel, never a real user's scope (BON-630 contract).
    canResendConfirmation: hasAnyPermission(Permission.WRITE_BOOKINGS),

    // Optional despite the contract requiring it: FE and API deploy separately,
    // and a bare read would white-screen the page against an older API.
    cutoffPassed: booking.rebook?.cutoffPassed ?? false,

    rebookOpen,
    refundOpen,
    cancelOpen,
    resendConfirmationOpen,
    openRebook: () => setRebookOpen(true),
    closeRebook: () => setRebookOpen(false),
    closeRefund: () => setRefundOpen(false),
    closeCancel: () => setCancelOpen(false),
    openResendConfirmation: () => setResendConfirmationOpen(true),
    closeResendConfirmation: () => setResendConfirmationOpen(false),

    // Must agree with `retireAffordance`, which labels the button from the same
    // flag — a disagreement opens the wrong dialog.
    onRetire: () => (booking.isComp ? setCancelOpen(true) : setRefundOpen(true)),

    activityRefreshToken,
    onBookingActionCompleted,

    resendConfirmationAlert,
    dismissResendConfirmationAlert: () => setResendConfirmationAlert(null),
    onResendConfirmationDone: (outcome) => {
      setResendConfirmationOpen(false);
      if (outcome.toast) showToast({ message: outcome.toast, tone: 'success' });
      // Set even when it is null, so a second attempt clears the first's news.
      setResendConfirmationAlert(outcome.alert ?? null);
      if (outcome.refresh) onBookingActionCompleted();
    },
  };
}
